import React, { memo } from "react";
import {
  Group,
  Text,
  Box,
  Image,
  Badge,
  UnstyledButton,
  Collapse,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronRight,
  IconPhotoOff,
} from "@tabler/icons-react";
import type { MenuItem } from "./types";
import { getItemCategories } from "./hooks/useMenuV2";
import { ItemEditor } from "./ItemEditor";

interface Props {
  item: MenuItem;
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (itemId: string, updates: Partial<MenuItem>) => Promise<void>;
  onDelete: (itemId: string) => Promise<void>;
}

const formatPrice = (p: MenuItem["price"]): string => {
  if (p === null || p === undefined || p === "") return "—";
  const n = typeof p === "number" ? p : parseFloat(p);
  if (Number.isNaN(n)) return String(p);
  return `¥${n.toLocaleString()}`;
};

const ItemRowInner: React.FC<Props> = ({
  item,
  expanded,
  onToggle,
  onUpdate,
  onDelete,
}) => {
  const cats = getItemCategories(item);
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <Box
      style={{
        borderBottom: "1px solid var(--mantine-color-gray-2)",
        background: expanded ? "var(--mantine-color-gray-0)" : "white",
      }}
    >
      <UnstyledButton
        onClick={onToggle}
        style={{
          width: "100%",
          padding: isMobile ? "10px 12px" : "10px 16px",
        }}
      >
        <Group wrap="nowrap" gap={isMobile ? "xs" : "md"}>
          {expanded ? (
            <IconChevronDown size={14} color="var(--mantine-color-gray-6)" style={{ flexShrink: 0 }} />
          ) : (
            <IconChevronRight size={14} color="var(--mantine-color-gray-6)" style={{ flexShrink: 0 }} />
          )}
          {!isMobile && (
            item.imageUrl ? (
              <Image
                src={item.imageUrl}
                alt={item.name}
                w={40}
                h={40}
                radius="sm"
                fit="cover"
              />
            ) : (
              <Box
                w={40}
                h={40}
                style={{
                  background: "var(--mantine-color-gray-2)",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IconPhotoOff size={16} color="var(--mantine-color-gray-5)" />
              </Box>
            )
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} lineClamp={1}>
              {item.name || <Text span c="dimmed" fs="italic">Untitled</Text>}
            </Text>
            {item.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {item.description}
              </Text>
            )}
          </div>
          {!isMobile && (
            <Group gap={4} wrap="nowrap">
              {cats.slice(0, 2).map((c) => (
                <Badge key={c} size="xs" variant="light" color="gray">
                  {c}
                </Badge>
              ))}
              {cats.length > 2 && (
                <Badge size="xs" variant="light" color="gray">
                  +{cats.length - 2}
                </Badge>
              )}
            </Group>
          )}
          <Text size="sm" c="dimmed" w={isMobile ? 60 : 70} ta="right" style={{ flexShrink: 0 }}>
            {formatPrice(item.price)}
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse in={expanded}>
        {/* Only mount the editor when the row is expanded so we don't fire
            per-item network requests (ingredients, etc.) for every row on the page. */}
        {expanded && (
          <Box px={isMobile ? "sm" : "md"} pb="md">
            <ItemEditor item={item} onUpdate={onUpdate} onDelete={onDelete} />
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

// Memoize so untouched rows don't re-render when sibling items update
export const ItemRow = memo(ItemRowInner, (prev, next) => {
  return (
    prev.item === next.item &&
    prev.expanded === next.expanded
  );
});
